import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CalendarRange,
  Gift,
  Handshake,
  Pencil,
  Plus,
} from 'lucide-react';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { KpiCard } from '@/shared/components/kpi-card';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { AnimatedIcon } from '@/shared/components/animated-icon';
import { Pagination } from '@/shared/ui/pagination';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { SegmentedControl, type SegmentedOption } from '@/shared/ui/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { useAuth } from '@/shared/lib/auth';
import { usePassbook } from '@/shared/lib/passbook';
import { canConfigureMemberGetMember } from '@/shared/lib/navigation';
import {
  mgmKeys,
  REFERRAL_CODE_LENGTH,
  setMgmCampaignActive,
  useMgmCampaigns,
  useMgmReferrals,
  useMgmSummary,
} from '../api/use-mgm-admin';
import { campaignPhase, type CampaignPhase } from '../model/campaign-form';
import { rewardSentence } from '../model/copy';
import { formatReward, programLabel } from '../model/program';
import { formatRate } from '../model/referral-status';
import type { MgmCampaign, ReferralStatus } from '../model/types';
import { MgmFunnel } from './mgm-funnel';
import { MgmHealthCard } from './mgm-health-card';
import { MgmReferralDialog } from './mgm-referral-dialog';
import { MgmReferralCard } from './mgm-referral-card';
import { METRIC_ICON, REFERRAL_ICON, ROLE_ICON } from './status-icons';


const PAGE_SIZE = 20;

const PHASE: Record<CampaignPhase, { label: string; variant: 'success' | 'info' | 'secondary' | 'warning' }> = {
  live: { label: 'Em vigor', variant: 'success' },
  scheduled: { label: 'Agendada', variant: 'info' },
  ended: { label: 'Encerrada', variant: 'warning' },
  inactive: { label: 'Inativa', variant: 'secondary' },
};

type StatusFilter = ReferralStatus | 'all';

const STATUS_OPTIONS: SegmentedOption<StatusFilter>[] = [
  { value: 'all', label: 'Todas' },
  { value: 'indicated', label: 'Indicadas', icon: REFERRAL_ICON.indicated },
  { value: 'registered', label: 'Cadastradas', icon: REFERRAL_ICON.registered },
  { value: 'converted', label: 'Convertidas', icon: REFERRAL_ICON.converted },
];

function formatDate(value: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleDateString('pt-BR');
}

function validationText(c: MgmCampaign): string {
  const v = c.purchaseRule.validation;
  const parts: string[] = [];
  if (v.mode === 'hold') parts.push(`compra vale depois de ${v.holdHours}h sem cancelamento`);
  else parts.push(v.clawback ? `compra vale na hora, com estorno em até ${v.clawbackDays} dia(s)` : 'compra vale na hora');
  if (c.purchaseRule.minAmount) {
    parts.push(`mínimo de ${c.purchaseRule.minAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`);
  }
  if (c.purchaseRule.withinDays) parts.push(`até ${c.purchaseRule.withinDays} dias após o cadastro`);
  return parts.join(' · ');
}

function needsPurchase(c: MgmCampaign): boolean {
  return (['referrer', 'referred'] as const).some(
    (role) => c.rewards[role].enabled && c.rewards[role].conditions.some((x) => x.type === 'first_purchase'),
  );
}

/**
 * Indique e Ganhe — relatório geral (spec, seções 15 e 16), lista de
 * indicações e, para o master, a campanha e a saúde do worker.
 */
export function MgmOverviewPage() {
  const { user } = useAuth();
  const { idPassbook, passbook } = usePassbook();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isMaster = canConfigureMemberGetMember(user?.email, passbook);

  const campaigns = useMgmCampaigns(idPassbook);
  const list = useMemo(() => campaigns.data?.campaigns ?? [], [campaigns.data]);
  const [selectedId, setSelectedId] = useState<string>('');
  const campaign =
    list.find((c) => c._id === selectedId) ??
    list.find((c) => c._id === campaigns.data?.liveCampaignId) ??
    list[0] ??
    null;
  const campaignId = campaign?._id ?? '';

  const summary = useMgmSummary(idPassbook, campaignId);
  const [status, setStatus] = useState<StatusFilter>('all');
  const [codeInput, setCodeInput] = useState('');
  const [code, setCode] = useState('');
  const [page, setPage] = useState(0);
  // Busca por código: espera o usuário parar de digitar (e o backend só filtra código completo).
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = codeInput.trim();
      setCode(next.length === REFERRAL_CODE_LENGTH ? next : '');
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [codeInput]);
  const referrals = useMgmReferrals(
    idPassbook,
    { campaignId, status: status === 'all' ? '' : status, code, page, limit: PAGE_SIZE },
    Boolean(campaignId),
  );
  const [openId, setOpenId] = useState<string | null>(null);
  const [toggling, setToggling] = useState(false);

  const base = `/loja/${idPassbook}/indique-e-ganhe`;
  const s = summary.data;
  const program = campaigns.data?.program ?? null;

  const toggleActive = async (active: boolean) => {
    if (!campaign) return;
    setToggling(true);
    try {
      await setMgmCampaignActive(idPassbook, campaign._id, active);
      toast.success(active ? 'Campanha ativada.' : 'Campanha desativada.');
      await queryClient.invalidateQueries({ queryKey: mgmKeys.all(idPassbook) });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível alterar a campanha.');
    } finally {
      setToggling(false);
    }
  };

  const header = (
    <PageHeader
      title="Indique e ganhe"
      description="Clientes indicam clientes: acompanhe indicações, cadastros, primeiras compras e recompensas."
      actions={
        isMaster && program ? (
          <div className="flex gap-2">
            {campaign ? (
              <Button
                variant="secondary"
                onClick={() => navigate(`${base}/campanha/${campaign._id}`)}
                aria-label="Editar campanha"
              >
                <Pencil className="size-4" /> <span className="hidden sm:inline">Editar campanha</span>
              </Button>
            ) : null}
            <Button onClick={() => navigate(`${base}/campanha`)} aria-label="Nova campanha">
              <Plus className="size-4" /> <span className="hidden sm:inline">Nova campanha</span>
            </Button>
          </div>
        ) : null
      }
    />
  );

  if (campaigns.isLoading) {
    return (
      <>
        {header}
        <Skeleton className="h-40 rounded-2xl" />
      </>
    );
  }

  if (campaigns.data && !program) {
    return (
      <>
        {header}
        <EmptyState
          icon={Gift}
          title="Programa sem suporte ao Indique e Ganhe"
          description="Cartões de saldo pré-pago e gift card não participam: a recompensa usa selos, pontos ou cashback."
        />
      </>
    );
  }

  if (!campaign) {
    return (
      <>
        {header}
        <EmptyState
          icon={Handshake}
          title="Nenhuma campanha configurada"
          description={
            isMaster
              ? 'Crie a campanha para liberar o link de indicação dos clientes e o botão "Indique e ganhe" no app.'
              : 'A campanha de indicação desta loja ainda não foi configurada pela Fixe.'
          }
          action={
            isMaster ? (
              <Button onClick={() => navigate(`${base}/campanha`)}>
                <Plus className="size-4" /> Criar campanha
              </Button>
            ) : undefined
          }
        />
      </>
    );
  }

  const phase = campaignPhase(campaign);
  const cadastroHint = s ? `${formatRate(s.rates.registrationRate)} das indicações` : undefined;
  const conversaoHint = s ? `${formatRate(s.rates.conversionOverRegistered)} dos cadastros` : undefined;

  return (
    <>
      {header}

      {isMaster && campaigns.data && !campaigns.data.enabled ? (
        <Card className="mb-4 flex items-start gap-3 border-warning/60 bg-warning/5 p-4">
          <AlertTriangle className="mt-0.5 size-5 shrink-0 text-warning" />
          <p className="text-sm">
            O Indique e Ganhe está <strong>desligado na API</strong> (<code>mgm_enabled</code> no env.json). Nenhuma
            indicação é registrada e nenhuma recompensa é lançada até ligar.
          </p>
        </Card>
      ) : null}

      <Card className="mb-6 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">{campaign.name}</h2>
              <Badge variant={PHASE[phase].variant}>{PHASE[phase].label}</Badge>
              <Badge variant="outline">{programLabel(campaign.program)}</Badge>
            </div>
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <CalendarRange className="size-4" />
              {formatDate(campaign.startsAt)}
              {campaign.endsAt ? ` até ${formatDate(campaign.endsAt)}` : ' · sem data de término'}
            </p>
            <ul className="space-y-1 text-sm">
              {(['referrer', 'referred'] as const).map((role) =>
                campaign.rewards[role].enabled ? (
                  <li key={role} className="flex items-start gap-2">
                    <AnimatedIcon
                      icon={ROLE_ICON[role]}
                      motion={role === 'referrer' ? 'wiggle' : 'float'}
                      loop
                      className="size-4 text-primary"
                      wrapperClassName="mt-0.5"
                    />
                    <span className="min-w-0">{rewardSentence(
                      role,
                      formatReward(campaign.program, campaign.rewards[role].amount),
                      campaign.rewards[role].conditions.some((c) => c.type === 'first_purchase')
                        ? 'first_purchase'
                        : 'registered',
                      'admin',
                    )}</span>
                  </li>
                ) : null,
              )}
            </ul>
            {needsPurchase(campaign) ? (
              <p className="text-xs text-muted-foreground">Primeira compra: {validationText(campaign)}.</p>
            ) : null}
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:items-end">
            {list.length > 1 ? (
              <Select value={campaign._id} onValueChange={(v) => { setSelectedId(v); setPage(0); }}>
                <SelectTrigger className="w-full sm:w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {list.map((c) => (
                    <SelectItem key={c._id} value={c._id}>
                      {c.name} · {formatDate(c.startsAt)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
            {isMaster ? (
              <label className="flex items-center gap-2 text-sm">
                Ativa
                <Switch
                  checked={campaign.status === 'active'}
                  disabled={toggling}
                  onCheckedChange={(v) => void toggleActive(v)}
                />
              </label>
            ) : null}
          </div>
        </div>
      </Card>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <KpiCard index={0} icon={METRIC_ICON.referrers} label="Clientes indicadores" value={s?.referrers ?? 0} loading={summary.isLoading} />
        <KpiCard index={1} icon={METRIC_ICON.indicated} label="Indicações" value={s?.indicated ?? 0} loading={summary.isLoading} />
        <KpiCard index={2} icon={METRIC_ICON.registered} label="Cadastros" value={s?.registered ?? 0} hint={cadastroHint} loading={summary.isLoading} />
        <KpiCard index={3} icon={METRIC_ICON.converted} label="Conversões" value={s?.converted ?? 0} hint={conversaoHint} loading={summary.isLoading} />
        <KpiCard
          index={4}
          icon={METRIC_ICON.rewards}
          label="Recompensas concedidas"
          value={s?.rewards.granted ?? 0}
          hint={s ? `${s.rewards.referrer.granted} p/ indicador · ${s.rewards.referred.granted} p/ indicado` : undefined}
          loading={summary.isLoading}
          className="col-span-2 lg:col-span-1"
        />
      </div>

      <div className="mb-6 grid gap-4 lg:grid-cols-[2fr_1fr]">
        <MgmFunnel funnel={s?.funnel} loading={summary.isLoading} />
        <Card className="space-y-4 p-4 sm:p-5">
          <div>
            <p className="text-sm font-medium">Taxas</p>
            <p className="text-xs text-muted-foreground">Desempenho do cliente como canal de aquisição.</p>
          </div>
          {[
            ['Taxa de cadastro', s?.rates.registrationRate],
            ['Conversão sobre cadastros', s?.rates.conversionOverRegistered],
            ['Conversão sobre indicações', s?.rates.conversionOverIndicated],
          ].map(([label, value]) => (
            <div key={String(label)} className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-muted-foreground">{label}</span>
              <span className="text-xl font-semibold tabular-nums">{formatRate(value as number | null | undefined)}</span>
            </div>
          ))}
          <div className="border-t border-border/60 pt-3 text-sm">
            <p className="flex justify-between">
              <span className="text-muted-foreground">Total para indicadores</span>
              <span className="tabular-nums">{s?.rewards.referrer.grantedAmountText ?? '—'}</span>
            </p>
            <p className="flex justify-between">
              <span className="text-muted-foreground">Total para indicados</span>
              <span className="tabular-nums">{s?.rewards.referred.grantedAmountText ?? '—'}</span>
            </p>
          </div>
        </Card>
      </div>

      {isMaster ? (
        <div className="mb-6">
          <MgmHealthCard idPassbook={idPassbook} />
        </div>
      ) : null}

      <h2 className="mb-3 text-base font-semibold">Indicações</h2>
      {/* Mesmo layout da página de Leads: busca (aqui, pelo código de quem indicou) + filtro + cards. */}
      <SelectableListLayout
        search={codeInput}
        onSearchChange={(v) => setCodeInput(v.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, REFERRAL_CODE_LENGTH))}
        searchPlaceholder="Buscar pelo código de quem indicou"
        filtersRow={
          <SegmentedControl
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(0);
            }}
            options={STATUS_OPTIONS}
            size="sm"
            className="max-w-full"
          />
        }
        selectAllId="select-all-referrals"
        itemCount={referrals.data?.rows.length ?? 0}
        allOnPageSelected={false}
        someOnPageSelected={false}
        onToggleAllOnPage={() => undefined}
        hideSelection
        withFloatingBar={false}
        isLoading={referrals.isLoading}
        isEmpty={!referrals.isLoading && (referrals.data?.rows.length ?? 0) === 0}
        emptyTitle="Nenhuma indicação ainda"
        emptyDescription="Assim que alguém se identificar por um link de indicação, ela aparece aqui."
        footer={
          (referrals.data?.total ?? 0) > PAGE_SIZE ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              total={referrals.data?.total ?? 0}
              onPageChange={setPage}
              className="justify-center pt-2"
            />
          ) : null
        }
      >
        {(referrals.data?.rows ?? []).map((row, i) => (
          <li key={row.id}>
            <MgmReferralCard referral={row} index={i} idPassbook={idPassbook} onOpen={setOpenId} />
          </li>
        ))}
      </SelectableListLayout>

      <MgmReferralDialog idPassbook={idPassbook} referralId={openId} onClose={() => setOpenId(null)} />
    </>
  );
}
